import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TrendingUp, DollarSign, MousePointer, ShoppingBag, Award, Copy, Check, LogIn, Eye, EyeOff, ExternalLink, ChevronUp } from 'lucide-react';
import { affiliateApi } from '../lib/api';

const TIER_COLORS = {
  base: { bg: '#27272a', border: '#3f3f46', text: '#a1a1aa', label: 'Ambassador' },
  top: { bg: '#422006', border: '#854d0e', text: '#fbbf24', label: 'Top Performer' },
  vip: { bg: '#052e16', border: '#166534', text: '#22c55e', label: 'VIP Partner' },
};

function LoginForm({ onLogin }) {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !password.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await affiliateApi.login({ code: code.trim(), password, email: email.trim() });
      onLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Login failed';
      if (detail.includes('Invalid password')) {
        setError('Incorrect password. Please try again.');
      } else if (detail.includes('email on file')) {
        setIsFirstLogin(true);
        setError('First login: enter the email on file to set your password.');
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4" data-testid="affiliate-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Award className="w-8 h-8 text-[#22c55e] mx-auto mb-3" />
          <h1 className="font-heading text-3xl text-white uppercase tracking-wider">Affiliate Portal</h1>
          <p className="text-zinc-500 text-sm mt-2">Track your earnings, clicks, and conversions</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[10px] font-heading uppercase tracking-widest text-zinc-500 mb-1 block">Your Affiliate Code</label>
            <input value={code} onChange={e => setCode(e.target.value)} placeholder="Enter your code"
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-3 outline-none focus:border-[#22c55e]"
              data-testid="affiliate-code-input" />
          </div>
          <div>
            <label className="text-[10px] font-heading uppercase tracking-widest text-zinc-500 mb-1 block">
              Password {!isFirstLogin && <span className="text-zinc-700">(set on first login)</span>}
            </label>
            <div className="relative">
              <input value={password} onChange={e => setPassword(e.target.value)}
                type={showPw ? 'text' : 'password'} placeholder="Enter password"
                className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-3 pr-10 outline-none focus:border-[#22c55e]"
                data-testid="affiliate-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3 text-zinc-600">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {isFirstLogin && (
            <div>
              <label className="text-[10px] font-heading uppercase tracking-widest text-zinc-500 mb-1 block">Email on file (first login only)</label>
              <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@example.com"
                className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-3 outline-none focus:border-[#22c55e]"
                data-testid="affiliate-email-input" />
            </div>
          )}
          {error && <p className="text-red-400 text-sm border border-red-900/30 bg-red-950/20 p-2" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full py-3 bg-[#22c55e] text-black text-xs font-heading uppercase tracking-wider hover:bg-[#22c55e]/80 transition-colors disabled:opacity-50"
            data-testid="affiliate-login-btn">{loading ? 'Logging in...' : 'Access Dashboard'}</button>
          <p className="text-[10px] text-zinc-600 text-center">First time? Enter the email on file to set your password.</p>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ affiliateData, code }) {
  const [copied, setCopied] = useState(null);
  const { affiliate, tier_info, recent_sales, clicks_this_month, conversion_rate, tiers } = affiliateData;
  const tc = TIER_COLORS[affiliate.tier] || TIER_COLORS.base;

  const copyText = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const shareUrl = `${window.location.origin}?ref=${affiliate.code}`;
  const discountMsg = `Use code "${affiliate.code}" for 15% off at NinjAthlete!`;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-300" data-testid="affiliate-dashboard">
      {/* Header */}
      <section className="pt-24 pb-6 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Award className="w-5 h-5 text-[#22c55e]" />
                <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-[#22c55e] border border-[#22c55e]/30 px-3 py-1">Affiliate Portal</span>
                <span className="text-[10px] font-heading uppercase px-2 py-0.5 border"
                  style={{ borderColor: tc.border, color: tc.text, backgroundColor: tc.bg }}>
                  {tc.label}
                </span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl text-white">Welcome, {affiliate.name}</h1>
              <p className="text-zinc-500 text-sm mt-1">
                Your code: <span className="text-[#22c55e] font-mono font-bold">{affiliate.code}</span> &middot;
                {affiliate.commission_pct}% commission &middot; 15% customer discount
              </p>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-4 pb-20">
        {/* Share tools */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="share-tools">
          <div className="border border-zinc-800 p-4">
            <p className="text-[10px] font-heading uppercase tracking-widest text-zinc-500 mb-2">Referral Link</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs text-zinc-400 font-mono bg-zinc-900 p-2 truncate">{shareUrl}</code>
              <button onClick={() => copyText(shareUrl, 'link')}
                className={`p-2 border transition-all ${copied === 'link' ? 'border-[#22c55e] text-[#22c55e]' : 'border-zinc-800 text-zinc-500 hover:text-white'}`}
                data-testid="copy-referral-link">{copied === 'link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button>
            </div>
          </div>
          <div className="border border-zinc-800 p-4">
            <p className="text-[10px] font-heading uppercase tracking-widest text-zinc-500 mb-2">Share Message</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs text-zinc-400 font-mono bg-zinc-900 p-2 truncate">{discountMsg}</code>
              <button onClick={() => copyText(discountMsg, 'msg')}
                className={`p-2 border transition-all ${copied === 'msg' ? 'border-[#22c55e] text-[#22c55e]' : 'border-zinc-800 text-zinc-500 hover:text-white'}`}
                data-testid="copy-share-msg">{copied === 'msg' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="affiliate-stats">
          <div className="border border-zinc-800 p-4">
            <MousePointer className="w-4 h-4 text-[#3b82f6] mb-2" />
            <p className="text-2xl font-heading text-white">{affiliate.total_clicks}</p>
            <p className="text-[10px] font-mono text-zinc-600 uppercase">Total Clicks</p>
            <p className="text-[10px] font-mono text-zinc-700">{clicks_this_month} this month</p>
          </div>
          <div className="border border-zinc-800 p-4">
            <ShoppingBag className="w-4 h-4 text-[#22c55e] mb-2" />
            <p className="text-2xl font-heading text-white">{affiliate.total_orders}</p>
            <p className="text-[10px] font-mono text-zinc-600 uppercase">Conversions</p>
            <p className="text-[10px] font-mono text-zinc-700">{conversion_rate}% rate</p>
          </div>
          <div className="border border-zinc-800 p-4">
            <DollarSign className="w-4 h-4 text-[#f59e0b] mb-2" />
            <p className="text-2xl font-heading text-white">${affiliate.total_commission.toFixed(2)}</p>
            <p className="text-[10px] font-mono text-zinc-600 uppercase">Total Earned</p>
            <p className="text-[10px] font-mono text-zinc-700">${affiliate.total_paid.toFixed(2)} paid</p>
          </div>
          <div className="border border-zinc-800 p-4">
            <TrendingUp className="w-4 h-4 text-[#E63946] mb-2" />
            <p className="text-2xl font-heading text-white">${affiliate.pending_payout.toFixed(2)}</p>
            <p className="text-[10px] font-mono text-zinc-600 uppercase">Pending Payout</p>
            <p className="text-[10px] font-mono text-zinc-700">${affiliate.total_revenue.toFixed(2)} revenue</p>
          </div>
        </div>

        {/* Tier progress */}
        <div className="border border-zinc-800 p-5 mb-8" data-testid="tier-progress">
          <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-4">Commission Tiers</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.entries(tiers).map(([key, t]) => {
              const active = affiliate.tier === key;
              const tierColor = TIER_COLORS[key];
              return (
                <div key={key}
                  className={`p-4 border transition-all ${active ? 'border-2' : 'border border-zinc-800/50 opacity-60'}`}
                  style={active ? { borderColor: tierColor.border, backgroundColor: tierColor.bg } : {}}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-heading uppercase tracking-wider" style={{ color: tierColor.text }}>{t.label}</span>
                    {active && <Check className="w-4 h-4" style={{ color: tierColor.text }} />}
                  </div>
                  <p className="text-2xl font-heading text-white">{t.commission_pct}%</p>
                  <p className="text-[10px] text-zinc-500">commission per sale</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent sales */}
        <div className="border border-zinc-800 p-5" data-testid="recent-sales">
          <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-4">Recent Sales</h3>
          {recent_sales.length > 0 ? (
            <div className="space-y-2">
              {recent_sales.map((s, i) => (
                <div key={s.date} className="flex items-center justify-between px-4 py-3 bg-zinc-900/30 border border-zinc-800/30">
                  <div>
                    <p className="text-sm text-white">Order &middot; ${s.order_total.toFixed(2)}</p>
                    <p className="text-[10px] text-zinc-600 font-mono">{new Date(s.date).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-1 text-[#22c55e]">
                    <ChevronUp className="w-3 h-3" />
                    <span className="text-sm font-heading">+${s.commission.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-600 text-sm text-center py-6">No sales yet. Share your code to start earning!</p>
          )}
        </div>
      </main>
    </div>
  );
}

export default function AffiliatePortalPage() {
  const [searchParams] = useSearchParams();
  const [session, setSession] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (loginData) => {
    setSession(loginData);
    setLoading(true);
    try {
      const { data } = await affiliateApi.getDashboard(loginData.affiliate.code, loginData.token);
      setDashboardData(data);
    } catch (err) {
      console.error('Dashboard load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!session) {
    return <LoginForm onLogin={handleLogin} />;
  }

  if (loading || !dashboardData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-24 flex items-center justify-center">
        <div className="animate-pulse text-zinc-500 text-sm font-mono">Loading dashboard...</div>
      </div>
    );
  }

  return <Dashboard affiliateData={dashboardData} code={session.affiliate.code} />;
}
